import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { authRoutesAkto, registerAuthGuard, useAuthentificationStore } from '@/features/auth/authentification'
import { impersoRoutes } from '@/features/auth/impersonnalisation'
import { contextAppRoutes, registerContextGuard, registerPermissionsGuard } from '@/features/auth/context-app'
import { DispositifLibelleCreation, DispositifSousDipositif, type Dispositif } from '@/features/domaine/types/opco'

/* Injecte les services d'afficahe des demandes */
import demande from '@/features/interface/demande/DemandeView.vue'
import { useDemandeRoutingService } from '@/features/interface/demande/shared/composables/useDemandeRoutingService'

/* Injecte les services de routage de recherche */
import '@/features/interface/recherches/demande/useRechercheDemandeRoutingService'
import '@/features/interface/recherches/dossier/useRechercheDossierRoutingService'
import { useDossierRoutingService } from '@/features/interface/detail/dossier/useDossierRoutingService'
import DossierDetailView from '@/features/interface/detail/dossier/DossierDetailView.vue'
import { useVirementRoutingService } from '@/features/interface/detail/virement/useVirementRoutingService'
import VirementDetailView from '@/features/interface/detail/virement/VirementDetailView.vue'
import { useFactureRoutingService } from '@/features/interface/detail/facture/useFactureRoutingService'
import FactureDetailView from '@/features/interface/detail/facture/FactureDetailView.vue'
import AccessDeniedView from '@/features/interface/shared/access-denied/AccessDeniedView.vue'
import MenuUtilisateurView from '@/features/interface/profil-utilisateur/ProfilUtilisateurView.vue'

// Pour test. A supprimer en prod
import AideContactComponent from '@/features/interface/help/AideContactComponent.vue'
import HomeView from '@/features/interface/home/HomeView.vue'
import OnboardingView from '@/features/interface/onboarding/OnboardingView.vue'
import ContactsSectionView from '@/features/interface/profil-utilisateur/contacts/ContactsSection.vue'
import ProfilSectionView from '@/features/interface/profil-utilisateur/profil/ProfilSection.vue'
import RattachementsView from '@/features/interface/profil-utilisateur/rattachements/RattachementsView.vue'
import RechercheDemandeView from '@/features/interface/recherches/demande/DemandeView.vue'
import '@/features/interface/recherches/demande/useRechercheDemandeRoutingService'
import RechercheDossierView from '@/features/interface/recherches/dossier/DossierView.vue'
import '@/features/interface/recherches/dossier/useRechercheDossierRoutingService'
import FactureView from '@/features/interface/recherches/facture/FactureView.vue'
import { injectLoadRechercheService } from '@/features/interface/recherches/rechercheRoutingServiceProvider'
import VirementView from '@/features/interface/recherches/virement/VirementView.vue'

// Pour test. A supprimer en prod
import { registerPopupAutoGuard } from '@/features/application/popup-auto/registerPopupAutoGuard'
import { AuthAktoEtats } from '@/features/auth/authentification/modeles'
import HabilitationsAdminInitializer from '@/features/interface/profil-utilisateur/admin/HabilitationsAdminInitializer.vue'
import TasksView from '@/views/TasksView.vue'
import './RouteMeta'
import { registerNavigationEvents } from './registerNavigationEvents'
import { routesPagesTests } from './index.tests'

const BASE_CONTEXT_URL = '/:context(adherent|prestataire)?'

export const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: async () => {
      const appState = useAuthentificationStore()
      const estUtilisateurChargé = appState.etat === AuthAktoEtats.Authentifié
      return estUtilisateurChargé ? HomeView : OnboardingView
    },
    meta: {
      label: 'Accueil',
      icon: 'home',
      order: 1,
    },
  },
  {
    path: '/profil',
    name: 'profil',
    component: demande,
  },
  {
    path: `${BASE_CONTEXT_URL}/dossier/:code`,
    name: 'dossier-detail',
    component: DossierDetailView,
    beforeEnter: async (to) => {
      const { loadStoreWithKey } = useDossierRoutingService()
      return await loadStoreWithKey((to.params as any).code)
    },
    meta: {
      requiresAuth: true,
      requiresContext: true,
    },
  },
  {
    path: `${BASE_CONTEXT_URL}/facture/:code`,
    name: 'facture-detail',
    component: FactureDetailView,
    beforeEnter: async (to) => {
      const { loadStoreWithKey } = useFactureRoutingService()
      return await loadStoreWithKey((to.params as any).code)
    },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: 'Détail de la facture',
    },
  },
  {
    path: `${BASE_CONTEXT_URL}/virement/:code`,
    name: 'virement-detail',
    component: VirementDetailView,
    beforeEnter: async (to) => {
      const { loadStoreWithKey } = useVirementRoutingService()
      return await loadStoreWithKey((to.params as any).code)
    },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: 'Détail du virement',
    },
  },
  {
    path: `${BASE_CONTEXT_URL}/dossiers`,
    name: 'recherche-dossier',
    component: RechercheDossierView,
    beforeEnter: async (to) => {
      const { loadStoreFromLocationQuery } = injectLoadRechercheService('dossier')
      return await loadStoreFromLocationQuery(to.query)
    },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      // Todo : Verifier les permissions avant
      toggleContextResolver: 'recherche-dossier',
      label: 'Dossiers',
      icon: 'folder-open',
      order: 2,
    },
  },
  {
    path: `${BASE_CONTEXT_URL}/brouillons`,
    name: 'recherche-demande',
    component: RechercheDemandeView,
    beforeEnter: async (to) => {
      const { loadStoreFromLocationQuery } = injectLoadRechercheService('demande')
      return await loadStoreFromLocationQuery(to.query)
    },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      toggleContextResolver: 'recherche-demande',
      label: 'Brouillons',
      icon: 'edit',
      order: 3,
    },
  },
  {
    path: `${BASE_CONTEXT_URL}/demande/:dispositif/:id`,
    name: 'demande',
    component: demande,
    beforeEnter: async (to) => {
      const { loadStoreWithId } = useDemandeRoutingService()
      const disposif = (to.params['dispositif'] as string).toUpperCase() as Dispositif
      const statut = await loadStoreWithId(disposif, parseInt((to.params as any).id))
      if (statut === 'Accces Denied') {
        return { name: 'access-denied', query: { to: to.fullPath } }
      }
      return statut === 'Ok'
    },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: (route) => {
        const dispositif = (route.params['dispositif'] as string).toUpperCase() as Dispositif
        const { getStore } = useDemandeRoutingService()
        const store = getStore(dispositif)
        const libelleDispositif = DispositifLibelleCreation[dispositif]
        if ('sousDispositif' in store.model! && store.model.sousDispositif) {
          const sousDispositifs = DispositifSousDipositif[dispositif]
          if (!sousDispositifs) return libelleDispositif
          const key = store.model.sousDispositif as keyof typeof sousDispositifs
          return [libelleDispositif, sousDispositifs[key]]
        } else {
          return libelleDispositif
        }
      },
    },
  },
  {
    path: `${BASE_CONTEXT_URL}/utilisateur`,
    component: MenuUtilisateurView,
    name: 'Menu utilisateur',
    redirect: { name: 'Profil' },
    meta: {
      requiresAuth: true,
      toggleContextResolver: 'Menu utilisateur',
    },
    children: [
      {
        path: 'profil',
        name: 'Profil',
        component: ProfilSectionView,
        meta: {
          requiresAuth: true,
          requiresContext: true,
          toggleContextResolver: 'Profil',
          label: 'Mon profil',
        },
      },
      {
        path: 'rattachements',
        name: 'Rattachements',
        component: RattachementsView,
        meta: {
          requiresAuth: true,
          requiresContext: true,
          toggleContextResolver: 'Rattachements',
          label: 'Mes rattachements',
        },
        // children: [
        //   {
        //     path: 'actifs',
        //     name: 'Actifs',
        //     component: RattachementsUtilisateurActifsView,
        //     meta: {
        //       requiresAuth: true,
        //       requiresContext: true,
        //       toggleContextResolver: 'Actifs',
        //       label: 'Actifs',
        //     },
        //   },
        //   {
        //     path: 'en-attente',
        //     name: 'En attente',
        //     component: RattachementsUtilisateurEnAttente,
        //     meta: {
        //       requiresAuth: true,
        //       requiresContext: true,
        //       toggleContextResolver: 'En attente',
        //       label: 'En attente',
        //     },
        //   },
        // ],
      },
      {
        path: 'administration/:mode?/:identifiant?',
        name: 'Administration',
        component: HabilitationsAdminInitializer,
        meta: {
          requiresAuth: true,
          requiresContext: true,
          requiresAdmin: true,
          toggleContextResolver: (route, _context, contextApp) => {
            const params = {
              ...route.params,
              context: contextApp,
            }
            return { ...route, params }
          },
          label: 'Administration des rattachements',
        },
      },
      {
        path: 'contacts',
        name: 'Contacts',
        component: ContactsSectionView,
        meta: {
          requiresAuth: true,
          requiresContext: true,
          toggleContextResolver: 'Contacts',
          label: 'Mes contacts',
        },
      },
    ],
  },
  {
    path: `${BASE_CONTEXT_URL}/taches`,
    name: 'tasks',
    component: TasksView,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      toggleContextResolver: 'tasks',
      label: 'Liste des tâches',
      icon: 'task-square',
      order: 4,
    },
  },
  {
    path: `${BASE_CONTEXT_URL}/factures`,
    name: 'factures',
    component: FactureView,
    beforeEnter: async (to) => {
      const { loadStoreFromLocationQuery } = injectLoadRechercheService('facture')
      return await loadStoreFromLocationQuery(to.query)
    },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      requirePermission: 'Facture',
      toggleContextResolver: 'factures',
      label: 'Factures',
      icon: 'bill',
      order: 5,
    },
  },
  {
    path: `${BASE_CONTEXT_URL}/virements`,
    name: 'virements',
    component: VirementView,
    beforeEnter: async (to) => {
      const { loadStoreFromLocationQuery } = injectLoadRechercheService('virement')
      return await loadStoreFromLocationQuery(to.query)
    },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      requirePermission: 'Facture',
      toggleContextResolver: 'virements',
      label: 'Virements',
      icon: 'payment',
      order: 6,
    },
  },
  {
    path: '/aide',
    name: 'help',
    component: AideContactComponent,
    meta: {
      requiresAuth: true,
      label: 'Aide & Contact',
      icon: 'device-message',
      order: 7,
    },
  },
  {
    path: '/access-denied',
    name: 'access-denied',
    component: AccessDeniedView,
    meta: {
      requiresAuth: true,
    },
  },
]

// Attention, route par défaut, toujours positionnée en dernier
const NOT_FOUND = {
  path: '/:pathMatch(.*)*',
  name: 'NotFound',
  redirect: { name: 'home' },
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...routes, ...routesPagesTests, ...authRoutesAkto, ...impersoRoutes, ...contextAppRoutes, NOT_FOUND],
})

registerAuthGuard(router)
registerContextGuard(router)
registerPermissionsGuard(router)
registerPopupAutoGuard(router)
registerNavigationEvents(router)

export default router
